var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useApolloClient } from '@apollo/client';
import { useLoading } from 'components/loading/LoadingProvider';
import { useSession } from 'components/session/SessionProvider';
import { createContext } from 'libs/react';
import { useContext } from 'libs/react';
import { useEffect } from 'libs/react';
import { useState } from 'libs/react';
import { Error } from 'components/Error';
import { Case } from 'components/util/Case';
import { MeQuery } from 'graphql/Me/queries/Me';
import { PermissionStatus } from 'graphql/types';
import { React } from 'libs/react';
import { useRoute } from 'components/router/Routing';
/**
 * @component UserProvider
 * @since 1.0.0
 */
export function UserProvider(props) {
    const client = useApolloClient();
    const { token } = useSession();
    const route = useRoute();
    const [ready, setReady] = useState(token == null);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    /**
     * @function fetch
     * @since 1.0.0
     * @hidden
     */
    function fetch() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield client.query({ query: MeQuery });
            if (result.data && result.data.me) {
                return result.data.me;
            }
            return null;
        });
    }
    /**
     * @function reload
     * @since 1.0.0
     * @hidden
     */
    function reload() {
        return __awaiter(this, void 0, void 0, function* () {
            setError(null);
            if (token) {
                try {
                    setUser(yield fetch());
                }
                catch (e) {
                    setError(e);
                    route.push('default.auth');
                }
            }
            else {
                setUser(null);
            }
            setReady(true);
        });
    }
    /**
     * @function unload
     * @since 1.0.0
     * @hidden
     */
    function unload() {
        return __awaiter(this, void 0, void 0, function* () {
            setUser(null);
            setReady(true);
            setError(null);
        });
    }
    /**
     * @function can
     * @since 1.0.0
     * @hidden
     */
    function can(permission, operator = 'and') {
        var _a, _b;
        if (operator == 'or') {
            return (((_a = user === null || user === void 0 ? void 0 : user.permissions.find((item) => {
                if (Array.isArray(permission)) {
                    return permission.includes(item.action.value);
                }
                return item.action.value == permission;
            })) === null || _a === void 0 ? void 0 : _a.status) == PermissionStatus.Allow || false);
        }
        else {
            if (Array.isArray(permission)) {
                return permission.every((value) => {
                    var _a;
                    return (((_a = user === null || user === void 0 ? void 0 : user.permissions.find((item) => {
                        return item.action.value == value;
                    })) === null || _a === void 0 ? void 0 : _a.status) == PermissionStatus.Allow || false);
                });
            }
            else {
                return (((_b = user === null || user === void 0 ? void 0 : user.permissions.find((item) => {
                    return item.action.value == permission;
                })) === null || _b === void 0 ? void 0 : _b.status) == PermissionStatus.Allow || false);
            }
        }
    }
    /**
     * @function isContactUser
     * @since 1.0.0
     * @hidden
     **/
    function isContactUser() {
        return user && user.contact ? true : false;
    }
    /**
     * @function isManager
     * @since 1.0.0
     * @hidden
     **/
    function isManager() {
        var _a;
        return user && isContactUser() && ((_a = user.contact) === null || _a === void 0 ? void 0 : _a.manager) ? true : false;
    }
    const { setLoaded } = useLoading();
    useEffect(() => {
        if (token) {
            reload();
        }
        else {
            unload();
        }
    }, [token]);
    useEffect(() => {
        setLoaded(ready);
    }, [ready]);
    const context = {
        user,
        reload,
        unload,
        can,
        isContactUser,
        isManager
    };
    return (React.createElement(UserContext.Provider, { value: context },
        React.createElement(Case, { is: error },
            React.createElement(Error, { title: "Ooops!", infos: "An error occured while trying to load the user data.", picto: "fal bug", error: error })),
        ready && props.children));
}
/**
 * @const UserContext
 * @since 1.0.0
 */
export const UserContext = createContext({
    user: null,
    reload: function () { },
    unload: function () { },
    can: function () {
        return false;
    },
    isContactUser: function () {
        return false;
    },
    isManager: function () {
        return false;
    }
});
/**
 * @function useUser
 * @since 1.0.0
 */
export function useUser() {
    return useContext(UserContext);
}
